/**
 * @fileoverview Table to display existing external accounts
 *
 * @author Ahsan Qureshi (aqureshi@yext.com)
 */

import {COLUMN_LABEL_ACCOUNT_NAME, COLUMN_LABEL_EMAIL,
  EXTERNAL_ACCOUNTS_LIST_HEADER, EXTERNAL_ACCOUNTS_MOBILE_LIST_HEADER, LINK_ANOTHER_NEW_ACCOUNT_BUTTON} from '/js/yext/externalaccounts/listingslaunch/copy';
import {MobileCardTable} from '/js/yext/externalaccounts/listingslaunch/mobilecardtable';
import {ExternalAccount} from '/js/yext/externalaccounts/models/externalaccount';
import {Publisher} from '/js/yext/externalaccounts/models/publisher';
import {ExternalAccountsContext} from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';
import {Button} from '/ui/components/button/button';
import {FilterBar, Section} from '/ui/components/filters/filterbar';
import {SearchFilterPill} from '/ui/components/filters/searchfilter';
import {Table, useSimpleSelection} from '/ui/components/table/table';
import {Types as TableTypes} from '/ui/components/table/table';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles
  from '/js/yext/externalaccounts/listingslaunch/styles/listingslaunchexternalaccounts.module.scss';

/**
 *
 * @param {{
 *   externalAccounts: !Array<ExternalAccount>,
 *   showSearch: boolean,
 *   handleAddNewAccount: function(string),
 *   publisher: Publisher,
 *   skipSetup?: boolean,
 * }} props
 * @return {React.ReactNode}
 */
export default function ExternalAccountsTable({skipSetup=false, ...props}) {
  const {
    externalAccounts,
    showSearch,
    handleAddNewAccount,
    publisher,
  } = props;

  const isSmalllViewport = useIsSmallViewport();
  const [searchTerm, setSearchTerm] = React.useState('');

  const {
    setPublisherIdToExternalAccount,
    publisherIdToExternalAccount,
    publisherIdToUserLinkedExternalAccounts,
  } = React.useContext(ExternalAccountsContext);

  const COLUMN_IDS = {
    ACCOUNT_NAME: 'name',
    EMAIL: 'email',
    IS_VALID: 'isValid',
  };

  const defaultColumns = [
    {
      id: COLUMN_IDS.ACCOUNT_NAME,
      label: COLUMN_LABEL_ACCOUNT_NAME,
    },
    {
      id: COLUMN_IDS.EMAIL,
      label: COLUMN_LABEL_EMAIL,
    },
  ];

  React.useEffect(() => {
    if (externalAccounts.length === 1 && externalAccounts[0].isValid) {
      const externalAccount = externalAccounts[0];
      setPublisherIdToExternalAccount(prev =>
        new Map(prev).set(publisher.id, externalAccount));
      // NOTE(jmay): to avoid ux bugs, if a user adds a linked account when
      // they previously only had one, reset the selected linked account state
    } else if (
      publisherIdToUserLinkedExternalAccounts.get(publisher.id)?.length > 0
        && externalAccounts.length === 2) {
      setPublisherIdToExternalAccount(prev =>
        new Map(prev).set(publisher.id, undefined));
    }
  }, [
    publisher,
    externalAccounts,
    setPublisherIdToExternalAccount,
    publisherIdToUserLinkedExternalAccounts,
  ]);

  const onSelection = React.useCallback(selection => {
    const rowId = selection.values().next().value;
    const selectedExternalAccount = skipSetup
      ? undefined
      : externalAccounts.filter(externalAccount => externalAccount.id === rowId)[0];
    if (selectedExternalAccount) {
      if (selectedExternalAccount.isValid) {
        setPublisherIdToExternalAccount(prev =>
          new Map(prev).set(publisher.id, selectedExternalAccount));
      } else {
        setPublisherIdToExternalAccount(prev =>
          new Map(prev).set(publisher.id, undefined));
      }
    }
  }, [setPublisherIdToExternalAccount, externalAccounts, publisher.id, skipSetup]);

  const isRowDisabled = id => {
    const account = externalAccounts.find(account => account.id === id);
    return !account || !account.isValid || skipSetup;
  };

  const externalAccountRows = (searchTerm !== '')
    ? getFilteredRows(externalAccounts, searchTerm)
    : externalAccounts;

  const selectExternalAccount = useSimpleSelection(
    externalAccountRows,
    onSelection,
    true,
    publisherIdToExternalAccount.get(publisher.id)?.id,
  );

  const rowSelectOptions =
    /** @type {TableTypes.RowSelectionOptions} */ ({
      ...selectExternalAccount,
      isDisabled: isRowDisabled,
    });

  const linkAccountUrl = publisher.oauthUrl;
  return (
    <div className={styles.externalAccountsTable}>
      {isSmalllViewport ? (
        <MobileCardTable
          label={EXTERNAL_ACCOUNTS_MOBILE_LIST_HEADER}
          columns={defaultColumns}
          dataRows={externalAccounts}
          rowSelectionOptions={
          externalAccounts.length === 1
              ? undefined
              : rowSelectOptions}
          handleAddNewAccount={handleAddNewAccount}
          linkAccountUrl={linkAccountUrl}
        />
      ) : (
        <div className={styles.tableContainer}>
          <Table
            label={EXTERNAL_ACCOUNTS_LIST_HEADER}
            columns={defaultColumns}
            filterBar={showSearch && (
              <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            )}
            dataRows={externalAccountRows}
            rowSelectionOptions={
            externalAccounts.length === 1
                ? undefined
                : rowSelectOptions}
            rowCallToAction={dataRow => {
              if (!dataRow[COLUMN_IDS.IS_VALID]) {
                return (
                  <Button onClick={() => handleAddNewAccount(linkAccountUrl)}
                    disabled={skipSetup}>
                    {yext.msg('Relink Account')}
                  </Button>
                );
              }
              return null;
            }}
          />
        </div>
      )}
      <div>
        <Button pendoId="listings-launch-flow-linking-add-another-account"
          onClick={() => handleAddNewAccount(
            linkAccountUrl)}
          disabled={skipSetup}
        >
          {LINK_ANOTHER_NEW_ACCOUNT_BUTTON}
        </Button>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   searchTerm: string,
 *   setSearchTerm: function(string),
 * }} props
 * @return {React.ReactNode}
 */
function SearchBar({searchTerm, setSearchTerm}) {
  const [currentSearchTerm, setCurrentSearchTerm] = React.useState(searchTerm);

  return (
    <FilterBar>
      <Section>
        <SearchFilterPill
          searchTerm={currentSearchTerm}
          onChange={value => setCurrentSearchTerm(value)}
          onApply={() => setSearchTerm(currentSearchTerm)}
          onCancel={() => setCurrentSearchTerm(searchTerm)}
          onClear={() => {
            setSearchTerm('');
            setCurrentSearchTerm('');
          }}
        />
      </Section>
    </FilterBar>
  );
}

function getFilteredRows(externalAccounts, searchTerm) {
  const finalTerm = searchTerm.toLowerCase();
  return externalAccounts.filter(ea => ea.name.toLowerCase().includes(finalTerm));
}
